/**
 * Orders list — "Orders List Redesign" mock.
 *
 * Restructure, not rebuild: the grid table, the apply-on-confirm filter
 * popovers, the KPI triage and the sessionStorage restore all survive. What
 * changed is the expression — Tailwind is gone in favour of the shell's own
 * tokens (so dark mode is zero code), filter state reads back as removable
 * chips, the table gains real semantics and keyboard rows, and below 768px the
 * same rows render as cards instead of a sideways table.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ConfigProvider, Modal, Skeleton, DatePicker, Popover, Input, Button, Checkbox } from 'antd';
import {
  SearchOutlined, EyeOutlined, EditOutlined, DeleteOutlined,
  SyncOutlined, CheckCircleOutlined, CheckOutlined,
  DownloadOutlined, CloseOutlined, ArrowRightOutlined, FilterOutlined, CalendarOutlined,
  ExclamationCircleOutlined, DownOutlined, TableOutlined,
} from '@ant-design/icons';
import { PDFDownloadLink } from '@react-pdf/renderer';
import dayjs from 'dayjs';
import useAxiosClient from '../axios-client.js';
import { useStateContext } from '../contexts/ContextProvider.jsx';
import Permission from '../components/Util/Permission.jsx';
import ExportOrderExcel from '../components/Order/ExportOrderExcel';
import OrderInvoice from '../components/OrderInvoice/OrderInvoice';
import { OrderTypeEnum } from '../utils/enums/OrderTypeEnum.js';
import {
  settableStatuses,
  canChangeAnyStatus,
  EXIT_STATUSES,
  OPEN_STATUSES,
  filterableStatuses,
} from '../utils/enums/OrderStatusEnum';
import StatusChangeModal from '../components/Order/StatusChangeModal';
import { PRIMARY, SHELL_THEME as THEME, shellAntdTheme, STATUS, MONO } from '../shellTokens';
import {
  fmtBDT, fmtDate, fmtDateTime, fmtDateCell, hexToRgba, statusMeta, isOverdue,
  StatusBadge, ChannelBadge,
} from '../components/Order/orderPrimitives';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 10;

// One breakpoint at 768 (Tailwind's `md`) so the KPI grid and the table→cards
// switch flip at the same moment. Between 768 and 1024 the table is back but
// two columns are hidden by default — a preference the user can override.
const MOBILE_BP = 768;
const TABLET_BP = 1024;
const TABLET_HIDDEN = ['team', 'createdBy'];
// Floor before horizontal scrolling kicks in — below this the cells stop being
// readable. It never applies under 768, where cards take over.
const MIN_TABLE_WIDTH = 820;

// Persisted (per-tab) Orders list view — filters/page/sort/density/columns plus
// the last-opened order — so navigating into a detail and back restores where
// you were.
const VIEW_KEY = 'ordersView';
const loadOrdersView = () => {
  try { return JSON.parse(sessionStorage.getItem(VIEW_KEY)) || {}; } catch { return {}; }
};

/**
 * The ten tracks. `locked` columns can't be hidden (an order without its id or
 * its actions is not a row); the other eight are the Columns popover's list.
 * Fluid `minmax(0,…fr)` widths let a hidden column's share flow to the
 * survivors instead of leaving a gap, and let every cell truncate rather than
 * overflow — the full value is in a title tooltip.
 */
const COLUMNS = [
  { key: 'id', label: 'Order ID', width: 'minmax(0,0.85fr)', sortable: true, filter: 'id', locked: true },
  { key: 'channel', label: 'Channel', width: 'minmax(0,1fr)', filter: 'channel' },
  { key: 'orderedBy', label: 'Ordered By', width: 'minmax(0,1.6fr)', filter: 'orderedBy' },
  { key: 'team', label: 'Team', width: 'minmax(0,1fr)', filter: 'team' },
  { key: 'createdBy', label: 'Submitted', width: 'minmax(0,1.1fr)', filter: 'createdBy' },
  { key: 'createdAt', label: 'Date', width: 'minmax(0,0.85fr)', sortable: true },
  { key: 'deliveryDate', label: 'Delivery', width: 'minmax(0,0.85fr)', sortable: true, filter: 'delivery' },
  { key: 'totalAmount', label: 'Total', width: 'minmax(0,1fr)', sortable: true, align: 'right' },
  { key: 'status', label: 'Status', width: 'minmax(0,1.15fr)', filter: 'status' },
  { key: 'actions', label: 'Action', width: '88px', locked: true },
];

// Channel filter options; '' means both. Values match the API's `channel` param.
const CHANNEL_OPTIONS = [
  { label: 'All channels', value: '' },
  { label: 'Online', value: 'online' },
  { label: 'Offline', value: 'offline' },
];

/** Viewport width buckets, matched to the mock's two breakpoints. */
const useViewport = () => {
  const [w, setW] = useState(() => window.innerWidth);
  useEffect(() => {
    const onResize = () => setW(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return { isMobile: w < MOBILE_BP, isTablet: w >= MOBILE_BP && w < TABLET_BP };
};

function Orders() {
  const axiosClient = useAxiosClient();
  const { user, permissions, darkMode } = useStateContext();
  const navigate = useNavigate();
  const canChangeStatus = canChangeAnyStatus(permissions);
  const settable = settableStatuses(permissions);
  const { isMobile, isTablet } = useViewport();

  // Restore the list view on return from a detail page (same tab session).
  const v0 = useRef(loadOrdersView()).current;

  // -- List state --
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [meta, setMeta] = useState({ total: 0, current: 1, pageSize: DEFAULT_PAGE_SIZE });
  const [stats, setStats] = useState({ total: 0, overdue: 0, dueToday: 0, inProduction: 0, readyToDeliver: 0 });

  // -- Filters / query state --
  const [channel, setChannel] = useState(v0.channel || '');
  const [teamId, setTeamId] = useState(v0.teamId || '');
  const [teams, setTeams] = useState([]);
  const [text, setText] = useState(v0.text || { id: '', orderedBy: '', createdBy: '' });
  const [statusFilter, setStatusFilter] = useState(v0.statusFilter || []);
  const [deliveryRange, setDeliveryRange] = useState(
    v0.deliveryRange ? v0.deliveryRange.map((d) => (d ? dayjs(d) : null)) : null,
  );
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [sortField, setSortField] = useState(v0.sortField || null);
  const [sortOrder, setSortOrder] = useState(v0.sortOrder || null); // 'asc' | 'desc'
  const [page, setPage] = useState(v0.page || 1);
  const [pageSize, setPageSize] = useState(v0.pageSize || DEFAULT_PAGE_SIZE);
  const [activeCard, setActiveCard] = useState(v0.activeCard || null);
  const [lastViewedId, setLastViewedId] = useState(v0.lastViewedId ?? null);
  const lastViewedRef = useRef(null);

  // -- View preferences --
  const [density, setDensity] = useState(v0.density || 'comfortable');
  // null means "never chosen" — the tablet default applies until the user does.
  const [hiddenCols, setHiddenCols] = useState(v0.hiddenCols ?? null);
  const [openMenu, setOpenMenu] = useState(null); // 'columns' | 'rows' | 'sort' | 'bulk' | 'mobileFilters'

  // -- Selection --
  const [selected, setSelected] = useState({});
  // Below 768 an always-on checkbox column costs too much width, so selection is
  // a mode you enter deliberately.
  const [selectMode, setSelectMode] = useState(false);

  // -- Detail panel --
  const [panelId, setPanelId] = useState(null);
  const [panelOrder, setPanelOrder] = useState(null);
  const [panelLoading, setPanelLoading] = useState(false);
  const [statusPopoverId, setStatusPopoverId] = useState(null);

  // -- Status change (shared modal with the order page) --
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null);
  const [statusPreset, setStatusPreset] = useState(null);
  const [statusSubmitting, setStatusSubmitting] = useState(false);

  const panelRef = useRef(null);
  const panelCloseRef = useRef(null);
  const returnFocusRef = useRef(null);

  // -- Roving tabindex over the rows --
  const [focusedRow, setFocusedRow] = useState(0);
  const rowRefs = useRef([]);

  const [modal, contextHolder] = Modal.useModal();

  const compact = density === 'compact';
  // Between 768 and 1024 Team and Submitted drop by default; the moment the user
  // touches the Columns popover, their choice takes over at every width.
  const effectiveHidden = useMemo(
    () => hiddenCols ?? (isTablet ? TABLET_HIDDEN : []),
    [hiddenCols, isTablet],
  );
  const visibleCols = useMemo(
    () => COLUMNS.filter((c) => !effectiveHidden.includes(c.key)),
    [effectiveHidden],
  );
  const grid = useMemo(
    () => ['30px', ...visibleCols.map((c) => c.width)].join(' '),
    [visibleCols],
  );

  /**
   * The query string the list reads — and, with `paginate=false`, exactly what
   * Export sends. `includePage` is off for the export so it takes the whole
   * filtered set rather than the page you happen to be on.
   */
  const buildQuery = useCallback((includePage = true) => {
    const params = includePage ? { page, per_page: pageSize } : {};
    if (text.id) params.id = text.id;
    if (text.orderedBy) params.orderedBy = text.orderedBy;
    if (text.createdBy) params.createdBy = text.createdBy;
    if (channel) params.channel = channel;
    if (teamId) params.teamId = teamId;
    if (statusFilter.length) params.status = statusFilter.join(',');
    if (deliveryRange && deliveryRange.length === 2) {
      // Either bound may be null for an open-ended range (e.g. Overdue = up to
      // yesterday with no lower bound).
      if (deliveryRange[0]) params.startDate = dayjs(deliveryRange[0]).format('YYYY-MM-DD');
      if (deliveryRange[1]) params.endDate = dayjs(deliveryRange[1]).format('YYYY-MM-DD');
    }
    if (sortField && sortOrder) { params.sortField = sortField; params.sortOrder = sortOrder; }
    return Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== '' && v !== null)
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
      .join('&');
  }, [page, pageSize, text, channel, teamId, statusFilter, deliveryRange, sortField, sortOrder]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosClient.get(`/orders/getOrders/${user.id}?${buildQuery()}`);
      setOrders(res.data.data.map((d) => ({ ...d, key: d.id })));
      setMeta({
        total: res.data.meta.total,
        current: res.data.meta.current_page,
        pageSize: res.data.meta.per_page,
      });
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  }, [axiosClient, user.id, buildQuery]);

  // Stats depend only on channel and team, so they key on those alone — a
  // text/status/date/sort/page change fires one request, not two. The
  // AbortController is what stops a fast channel→team change from landing the
  // older response's counts.
  const fetchStats = useCallback(async (signal) => {
    try {
      const params = [];
      if (channel) params.push(`channel=${channel}`);
      if (teamId) params.push(`teamId=${teamId}`);
      const res = await axiosClient.get(`/orders/getStats/${user.id}?${params.join('&')}`, { signal });
      setStats(res.data);
    } catch (error) {
      if (error.code !== 'ERR_CANCELED' && error.name !== 'CanceledError') {
        console.error('Failed to fetch stats', error);
      }
    }
  }, [axiosClient, user.id, channel, teamId]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);
  useEffect(() => {
    const controller = new AbortController();
    fetchStats(controller.signal);
    return () => controller.abort();
  }, [fetchStats]);

  // Team options for the Team column filter. Ungated endpoint; the backend still
  // scopes what a non-admin can actually see (ADR 0001).
  useEffect(() => {
    let cancelled = false;
    axiosClient.get('/settings/teams/options')
      .then((res) => { if (!cancelled) setTeams(res.data?.data || []); })
      .catch(() => { /* filter just stays empty */ });
    return () => { cancelled = true; };
  }, [axiosClient]);

  // Persist the view so a detail round-trip restores it. dayjs → ISO for JSON.
  useEffect(() => {
    sessionStorage.setItem(VIEW_KEY, JSON.stringify({
      channel, teamId, text, statusFilter,
      deliveryRange: deliveryRange ? deliveryRange.map((d) => (d ? dayjs(d).toISOString() : null)) : null,
      sortField, sortOrder, page, pageSize, activeCard, lastViewedId, density, hiddenCols,
    }));
  }, [channel, teamId, text, statusFilter, deliveryRange, sortField, sortOrder, page, pageSize,
      activeCard, lastViewedId, density, hiddenCols]);

  // After rows load, scroll the last-opened order into view.
  useEffect(() => {
    if (!loading && lastViewedId != null && lastViewedRef.current) {
      lastViewedRef.current.scrollIntoView({ block: 'center' });
    }
  }, [loading, lastViewedId]);

  // Esc closes the panel (returning focus to the row that opened it), then
  // clears any selection.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (panelId != null) closePanel();
      else if (Object.keys(selected).length) setSelected({});
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // Opening the panel moves focus into it and keeps Tab inside; closing hands
  // focus back to the row.
  useEffect(() => {
    if (panelId == null || panelLoading) return;
    panelCloseRef.current?.focus();
  }, [panelId, panelLoading]);

  const trapTab = (e) => {
    if (e.key !== 'Tab' || !panelRef.current) return;
    const items = panelRef.current.querySelectorAll(
      'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  // -- Filter handlers (reset to page 1) --
  const resetPage = () => setPage(1);
  const applyText = (field, v) => { setText((t) => ({ ...t, [field]: v })); setActiveCard(null); resetPage(); setOpenFilterKey(null); };
  const applyStatus = (vals) => { setStatusFilter(vals); setActiveCard(null); resetPage(); setOpenFilterKey(null); };
  const applyDelivery = (range) => { setDeliveryRange(range); setActiveCard(null); resetPage(); setOpenFilterKey(null); };
  // Changing channel/team can drop selected rows out of view, so clear selection.
  const applyChannel = (v) => { setChannel(v); setSelected({}); setActiveCard(null); resetPage(); setOpenFilterKey(null); };
  const applyTeam = (v) => { setTeamId(v); setSelected({}); setActiveCard(null); resetPage(); setOpenFilterKey(null); };
  const clearOne = (fn) => { fn(); setActiveCard(null); resetPage(); setOpenFilterKey(null); };
  const clearFilters = () => {
    setText({ id: '', orderedBy: '', createdBy: '' });
    setStatusFilter([]);
    setDeliveryRange(null);
    setChannel('');
    setTeamId('');
    setActiveCard(null);
    resetPage();
  };
  // KPI card → table filter. Sets the same status/delivery predicate the card
  // counts, clears text filters, marks the card active, and resets to page 1.
  const goToBucket = (key, { status, range = null }) => {
    setText({ id: '', orderedBy: '', createdBy: '' });
    setStatusFilter(status);
    setDeliveryRange(range);
    setActiveCard(key);
    resetPage();
  };
  const filterActive = (type) => {
    if (type === 'status') return statusFilter.length > 0;
    if (type === 'delivery') return !!deliveryRange;
    if (type === 'channel') return !!channel;
    if (type === 'team') return !!teamId;
    return type ? !!text[type] : false;
  };
  const teamOptions = [{ label: 'All teams', value: '' }, ...teams.map((t) => ({ label: t.name, value: String(t.id) }))];
  const teamName = teams.find((t) => String(t.id) === String(teamId))?.name || teamId;

  const renderFilter = (h) => {
    if (h.filter === 'status') return <StatusFilterDropdown committed={statusFilter} onApply={applyStatus} onReset={() => clearOne(() => setStatusFilter([]))} />;
    if (h.filter === 'delivery') return <DateFilterDropdown committed={deliveryRange} onApply={applyDelivery} onReset={() => clearOne(() => setDeliveryRange(null))} />;
    if (h.filter === 'channel') return <ChoiceFilterDropdown committed={channel} options={CHANNEL_OPTIONS} onApply={applyChannel} />;
    if (h.filter === 'team') return <ChoiceFilterDropdown committed={teamId} options={teamOptions} onApply={applyTeam} />;
    return <TextFilterDropdown committed={text[h.filter]} placeholder={`Search ${h.label}`} onApply={(v) => applyText(h.filter, v)} onReset={() => clearOne(() => setText((t) => ({ ...t, [h.filter]: '' })))} />;
  };

  const toggleSort = (key) => {
    if (sortField === key) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else { setSortField(key); setSortOrder('asc'); }
    resetPage();
  };
  const clearSort = () => { setSortField(null); setSortOrder(null); resetPage(); };

  // -- Column visibility --
  const toggleColumn = (key) => {
    const current = effectiveHidden;
    setHiddenCols(current.includes(key) ? current.filter((k) => k !== key) : [...current, key]);
  };
  const resetColumns = () => setHiddenCols(null);

  // -- Selection handlers --
  const pageIds = orders.map((o) => o.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected[id]);
  const selectedCount = Object.keys(selected).length;
  const toggleSelect = (id) => setSelected((s) => {
    const next = { ...s };
    if (next[id]) delete next[id]; else next[id] = true;
    return next;
  });
  const toggleSelectAll = () => setSelected((s) => {
    const next = { ...s };
    if (allOnPageSelected) pageIds.forEach((id) => delete next[id]);
    else pageIds.forEach((id) => { next[id] = true; });
    return next;
  });

  // -- Row actions --
  const handleNewOrder = (type) => navigate('/orders/orderForm', { state: { orderType: type, update: false } });
  const handleEdit = (record) => navigate('/orders/editOrderForm', { state: { orderType: record.orderType, update: true, order: record } });
  const openFullPage = (id) => { setLastViewedId(id); navigate(`/orders/${id}`); };
  const handleDelete = (record) => modal.confirm({
    title: 'Delete order?',
    content: `Are you sure you want to delete order #${record.id}?`,
    okText: 'Delete',
    okType: 'danger',
    onOk: async () => {
      try {
        const res = await axiosClient.delete(`/orders/delete/${record.id}`);
        toast.warning(res.data.message);
        refreshAfterMutation();
      } catch (error) {
        toast.error(error.response?.data?.message || error.message);
      }
    },
  });

  const refreshAfterMutation = () => { fetchOrders(); fetchStats(); };

  // -- Status changes: one control vocabulary (the popover menu) for the bulk
  // bar, the row badge and the panel alike.
  // A status change from the list is the same transaction as one from the order
  // page: it opens the shared modal, which owns the reason, the hold category and
  // the per-line restock decisions (ADR 0006). It used to POST an empty comment
  // straight from the badge, which is how cancellations lost both their reason
  // and their stock.
  const setOneStatus = async (id, val) => {
    setStatusPopoverId(null);
    const row = orders.find((o) => o.id === id);
    // The modal needs the order's lines to ask about restocking; the list row
    // carries none, so the full record is fetched before it opens.
    try {
      const res = await axiosClient.get(`/orders/getOrder/${id}`);
      setStatusTarget(res.data.data);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
      return;
    }
    setStatusPreset(val ?? row?.status ?? null);
    setStatusModalOpen(true);
  };

  const submitStatusChange = async (payload) => {
    setStatusSubmitting(true);
    try {
      const res = await axiosClient.post('/orders/updateOrderStatus', {
        orderId: statusTarget.id,
        ...payload,
      });
      toast.success(res.data.message || 'Order status updated');
      setStatusModalOpen(false);
      refreshAfterMutation();
      if (panelId === statusTarget.id) {
        const fresh = await axiosClient.get(`/orders/getOrder/${statusTarget.id}`);
        setPanelOrder(fresh.data.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setStatusSubmitting(false);
    }
  };

  // Bulk never sets an exit — StatusMenu greys those out — so one shared comment
  // is all this needs.
  const bulkSetStatus = (val) => {
    setOpenMenu(null);
    const ids = Object.keys(selected);
    const label = statusMeta(val).label;
    let comment = '';
    modal.confirm({
      title: `Update ${ids.length} order${ids.length === 1 ? '' : 's'} to ${label}?`,
      content: (
        <Input.TextArea
          rows={2}
          placeholder="Comment (optional) — applied to every order"
          maxLength={200}
          onChange={(e) => { comment = e.target.value; }}
          style={{ marginTop: 10 }}
        />
      ),
      okText: 'Update',
      onOk: async () => {
        const results = await Promise.allSettled(
          ids.map((id) => axiosClient.post('/orders/updateOrderStatus', {
            orderId: id,
            newStatus: val,
            statusComment: comment.trim(),
          })),
        );
        const ok = results.filter((r) => r.status === 'fulfilled').length;
        if (ok) toast.success(`${ok} order(s) updated`);
        if (ok < ids.length) toast.warning(`${ids.length - ok} could not be updated`);
        setSelected({});
        refreshAfterMutation();
      },
    });
  };

  const bulkDelete = () => {
    const ids = Object.keys(selected);
    modal.confirm({
      title: `Delete ${ids.length} order${ids.length === 1 ? '' : 's'}?`,
      content: 'This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        const results = await Promise.allSettled(ids.map((id) => axiosClient.delete(`/orders/delete/${id}`)));
        const ok = results.filter((r) => r.status === 'fulfilled').length;
        if (ok) toast.warning(`${ok} order(s) deleted`);
        if (ok < ids.length) toast.error(`${ids.length - ok} could not be deleted`);
        setSelected({});
        refreshAfterMutation();
      },
    });
  };

  // -- Detail panel --
  const openPanel = useCallback(async (id, trigger) => {
    returnFocusRef.current = trigger || document.activeElement;
    setPanelId(id);
    setPanelOrder(null);
    setPanelLoading(true);
    try {
      const res = await axiosClient.get(`/orders/getOrder/${id}`);
      setPanelOrder(res.data.data);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
      setPanelId(null);
    } finally {
      setPanelLoading(false);
    }
  }, [axiosClient]);

  const closePanel = () => {
    setPanelId(null);
    setPanelOrder(null);
    returnFocusRef.current?.focus?.();
  };

  // -- Keyboard rows: one row in the tab order, arrows move, Enter opens, Space
  // selects, → reaches the row's actions.
  const focusRow = (idx) => {
    const clamped = Math.max(0, Math.min(orders.length - 1, idx));
    setFocusedRow(clamped);
    rowRefs.current[clamped]?.focus();
  };
  const onRowKeyDown = (e, idx, o) => {
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); focusRow(idx + 1); break;
      case 'ArrowUp': e.preventDefault(); focusRow(idx - 1); break;
      case 'Home': e.preventDefault(); focusRow(0); break;
      case 'End': e.preventDefault(); focusRow(orders.length - 1); break;
      case 'Enter': e.preventDefault(); openPanel(o.id, e.currentTarget); break;
      case ' ': e.preventDefault(); toggleSelect(o.id); break;
      case 'ArrowRight': {
        const first = e.currentTarget.querySelector('[data-row-action]');
        if (first) { e.preventDefault(); first.focus(); }
        break;
      }
      case 'ArrowLeft': e.preventDefault(); e.currentTarget.focus(); break;
      default: break;
    }
  };

  // -- Derived view data --
  // Actionable triage KPIs — each clicks through to the matching table filter
  // (see docs/orders-triage-stats-plan.md). OPEN comes from the shared enum so
  // the card, the filter and the server's count cannot drift apart; DRAFT is
  // excluded because a draft is not yet a committed order.
  const kpis = [
    { key: 'overdue', label: 'Overdue', value: stats.overdue, tint: '#ef4444', icon: <ExclamationCircleOutlined />, filter: { status: [...OPEN_STATUSES], range: [null, dayjs().subtract(1, 'day')] } },
    { key: 'dueToday', label: 'Due today', value: stats.dueToday, tint: '#f59e0b', icon: <CalendarOutlined />, filter: { status: [...OPEN_STATUSES], range: [dayjs(), dayjs()] } },
    { key: 'inProduction', label: 'In production', value: stats.inProduction, tint: '#6366f1', icon: <SyncOutlined />, filter: { status: [2, 3] } },
    // Finished but not handed over: READY plus the courier-booking step.
    { key: 'readyToDeliver', label: 'Ready to deliver', value: stats.readyToDeliver, tint: '#10b981', icon: <CheckCircleOutlined />, filter: { status: [5, 9] } },
  ];
  // Clicking the already-active card toggles its filter off.
  const onKpiClick = (k) => (activeCard === k.key ? clearFilters() : goToBucket(k.key, k.filter));
  const activeKpi = kpis.find((k) => k.key === activeCard);

  /**
   * One entry per applied predicate, each carrying its own clear function. The
   * header filter icons stay the way in; these chips are the way out. An
   * unfiltered list renders no strip at all, so it looks exactly as before.
   */
  const activeFilters = useMemo(() => {
    const chips = [];
    if (activeKpi) {
      chips.push({ key: 'card', value: activeKpi.label, tint: activeKpi.tint, onClear: clearFilters });
    }
    if (text.id) chips.push({ key: 'id', label: 'Order ID', value: text.id, onClear: () => clearOne(() => setText((t) => ({ ...t, id: '' }))) });
    if (text.orderedBy) chips.push({ key: 'orderedBy', label: 'Ordered By', value: text.orderedBy, onClear: () => clearOne(() => setText((t) => ({ ...t, orderedBy: '' }))) });
    if (text.createdBy) chips.push({ key: 'createdBy', label: 'Submitted', value: text.createdBy, onClear: () => clearOne(() => setText((t) => ({ ...t, createdBy: '' }))) });
    if (channel) chips.push({ key: 'channel', label: 'Channel', value: channel === 'online' ? 'Online' : 'Offline', onClear: () => applyChannel('') });
    if (teamId) chips.push({ key: 'team', label: 'Team', value: teamName, onClear: () => applyTeam('') });
    if (statusFilter.length) {
      const first = statusMeta(statusFilter[0]).label;
      chips.push({
        key: 'status',
        label: 'Status',
        value: statusFilter.length > 1 ? `${first} +${statusFilter.length - 1}` : first,
        onClear: () => clearOne(() => setStatusFilter([])),
      });
    }
    if (deliveryRange) {
      const [a, b] = deliveryRange;
      chips.push({
        key: 'delivery',
        label: 'Delivery',
        value: `${a ? dayjs(a).format('D MMM') : '…'} → ${b ? dayjs(b).format('D MMM') : '…'}`,
        mono: true,
        onClear: () => clearOne(() => setDeliveryRange(null)),
      });
    }
    return chips;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKpi, text, channel, teamId, teamName, statusFilter, deliveryRange]);

  const hasActiveFilters = activeFilters.length > 0;
  const sortChip = sortField && sortOrder
    ? { label: COLUMNS.find((c) => c.key === sortField)?.label || sortField, dir: sortOrder === 'asc' ? '↑' : '↓' }
    : null;
  const showChipStrip = hasActiveFilters || !!sortChip;

  // A human-readable restatement of the chips, for the export confirmation.
  const filterSummary = useMemo(
    () => activeFilters.map((c) => (c.label ? `${c.label} · ${c.value}` : c.value)),
    [activeFilters],
  );

  const totalPages = Math.max(1, Math.ceil(meta.total / (meta.pageSize || pageSize)));
  const rangeStart = meta.total === 0 ? 0 : (page - 1) * (meta.pageSize || pageSize) + 1;
  const rangeEnd = Math.min(page * (meta.pageSize || pageSize), meta.total);
  const goToPage = (p) => { setPage(Math.max(1, Math.min(totalPages, p))); setFocusedRow(0); };
  const changePageSize = (n) => { setPageSize(n); setPage(1); setOpenMenu(null); };

  const showEmpty = !loading && orders.length === 0;
  const showRows = !loading && orders.length > 0;

  const rootStyle = {
    ...Object.fromEntries(
      Object.entries(THEME[darkMode ? 'dark' : 'light']).map(([k, v]) => [`--${k}`, v]),
    ),
    '--primary': PRIMARY,
    '--mono': MONO,
  };

  const cellPad = compact ? '0 8px' : '9px 8px';
  const rowMinHeight = compact ? 38 : 52;

  return (
    <div className="et-orders" style={rootStyle}>
      <style>{`
        .et-orders{font-family:Outfit,system-ui,sans-serif;color:var(--text);display:flex;flex-direction:column;gap:14px;height:100%;min-height:0}
        .et-orders__row:hover{background:var(--hover)}
        .et-orders__row:focus-visible{outline:2px solid var(--primary);outline-offset:-2px}
        .et-orders__card:hover{border-color:var(--borderStrong)}
        .et-orders__kpi:hover{border-color:var(--borderStrong)}
        .et-orders__ghost:hover{background:var(--hover)}
        .et-orders__iconbtn:hover{background:var(--surface2);color:var(--text)}
        .et-orders__iconbtn--danger:hover{background:rgba(248,113,113,.12);color:${STATUS.error}}
        .et-orders__sortable:hover .et-orders__sorticon{color:var(--text)}
        .et-orders__skel{background:var(--surface2);border-radius:4px;animation:ovPulse 1.4s ease-in-out infinite}
        @keyframes ovPulse{0%,100%{opacity:.55}50%{opacity:.9}}
        @keyframes et-fadein{from{opacity:0}to{opacity:1}}
        @keyframes et-slidein{from{transform:translateX(24px);opacity:.4}to{transform:none;opacity:1}}
      `}</style>

      {/* Page title row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
        <div>
          <div style={{ font: '700 22px Outfit, sans-serif', letterSpacing: '-.01em' }}>Orders</div>
          <div style={{ font: '400 13px Outfit, sans-serif', color: 'var(--muted)', marginTop: 2 }}>
            {Number(stats.total || 0).toLocaleString()} order{stats.total === 1 ? '' : 's'}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Permission required={'EXPORT_ORDERS'}>
            <ExportOrderExcel
              query={buildQuery(false)}
              total={meta.total}
              filters={filterSummary}
              columns={visibleCols.filter((c) => c.key !== 'actions')}
            />
          </Permission>
          {/* One entry point: the channel is now chosen inside the order form. */}
          <Permission required={'CREATE_MARKETPLACE_ORDER'}>
            <button type="button" onClick={() => handleNewOrder(OrderTypeEnum.MARKETPLACE)} style={primaryBtn}>
              + New Order
            </button>
          </Permission>
        </div>
      </div>

      {/* KPI triage cards */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 10 }}>
        {kpis.map((k) => (
          <KpiCard key={k.key} kpi={k} active={activeCard === k.key} onClick={() => onKpiClick(k)} />
        ))}
      </div>

      <ConfigProvider theme={shellAntdTheme(darkMode)}>
        <div style={{
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14,
          overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0,
        }}>

          {/* Toolbar */}
          <div style={{
            padding: '10px 14px', borderBottom: '1px solid var(--border)', display: 'flex',
            alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
          }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <div style={{ font: '700 14.5px Outfit, sans-serif' }}>All orders</div>
              <div style={{ font: '600 11.5px var(--mono)', color: 'var(--dim)', padding: '2px 7px', borderRadius: 9, background: 'var(--surface2)' }}>
                {meta.total}
              </div>
            </div>

            {isMobile ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setOpenMenu('mobileFilters')} style={filterBtn}>
                  <FilterOutlined /> Filter
                  {activeFilters.length > 0 && (
                    <span style={{ font: '700 10.5px var(--mono)', background: PRIMARY, color: '#fff', padding: '1px 5px', borderRadius: 6 }}>
                      {activeFilters.length}
                    </span>
                  )}
                </button>
                <Popover
                  trigger="click"
                  open={openMenu === 'sort'}
                  onOpenChange={(o) => setOpenMenu(o ? 'sort' : null)}
                  content={
                    <MenuList
                      items={[
                        { key: '', label: 'Newest first' },
                        ...COLUMNS.filter((c) => c.sortable).flatMap((c) => [
                          { key: `${c.key}:asc`, label: `${c.label} ↑` },
                          { key: `${c.key}:desc`, label: `${c.label} ↓` },
                        ]),
                      ]}
                      active={sortField ? `${sortField}:${sortOrder}` : ''}
                      onSelect={(v) => {
                        setOpenMenu(null);
                        if (!v) { clearSort(); return; }
                        const [f, o] = v.split(':');
                        setSortField(f); setSortOrder(o); resetPage();
                      }}
                    />
                  }
                >
                  <button type="button" style={filterBtn}>
                    {sortChip ? <>{sortChip.label} <span style={{ color: PRIMARY }}>{sortChip.dir}</span></> : <>Sort <DownOutlined style={{ fontSize: 9, color: 'var(--muted)' }} /></>}
                  </button>
                </Popover>
                <button
                  type="button"
                  onClick={() => { setSelectMode((s) => !s); setSelected({}); }}
                  style={{ ...filterBtn, background: selectMode ? 'var(--activeBg)' : 'transparent', color: selectMode ? 'var(--activeText)' : 'var(--muted)' }}
                >
                  {selectMode ? 'Done' : 'Select'}
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Segmented
                  value={density}
                  onChange={setDensity}
                  options={[{ value: 'comfortable', label: 'Comfortable' }, { value: 'compact', label: 'Compact' }]}
                />
                <Popover
                  trigger="click"
                  open={openMenu === 'columns'}
                  onOpenChange={(o) => setOpenMenu(o ? 'columns' : null)}
                  content={
                    <ColumnsMenu
                      hidden={effectiveHidden}
                      onToggle={toggleColumn}
                      onReset={resetColumns}
                    />
                  }
                >
                  <button type="button" style={filterBtn}>
                    <TableOutlined /> Columns
                    <span style={{ font: '600 11px var(--mono)', color: 'var(--dim)' }}>
                      {visibleCols.length}/{COLUMNS.length}
                    </span>
                  </button>
                </Popover>
              </div>
            )}
          </div>

          {/* Filter chips — the way out of every applied predicate, sort included */}
          {showChipStrip && (
            <div style={{
              padding: '7px 14px', borderBottom: '1px solid var(--border)', background: 'var(--bg)',
              display: 'flex', alignItems: 'center', gap: 8, flexWrap: isMobile ? 'nowrap' : 'wrap',
              overflowX: isMobile ? 'auto' : 'visible',
            }}>
              {!isMobile && (
                <span style={{ font: '600 11px Outfit, sans-serif', color: 'var(--dim)', letterSpacing: '.05em', textTransform: 'uppercase', marginRight: 2 }}>
                  Filters
                </span>
              )}
              {activeFilters.map((c) => (
                <Chip key={c.key} label={c.label} value={c.value} tint={c.tint} mono={c.mono} onClear={c.onClear} />
              ))}
              {sortChip && (
                <Chip label="Sort" value={`${sortChip.label} ${sortChip.dir}`} tone="sort" onClear={clearSort} />
              )}
              {!isMobile && (
                <button type="button" onClick={() => { clearFilters(); clearSort(); }} style={{
                  marginLeft: 'auto', height: 26, padding: '0 10px', borderRadius: 7,
                  border: '1px solid var(--border)', background: 'transparent', color: 'var(--muted)',
                  font: '600 12px Outfit, sans-serif', cursor: 'pointer', flexShrink: 0,
                }}>
                  Clear all
                </button>
              )}
            </div>
          )}

          {/* Bulk action bar */}
          {selectedCount > 0 && (
            <div style={{
              padding: '6px 14px', borderBottom: '1px solid var(--border)', background: 'var(--activeBg)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <span style={{ font: '600 13px Outfit, sans-serif' }}>{selectedCount} selected</span>
                <button type="button" onClick={() => setSelected({})} style={linkBtn}>Clear</button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {canChangeStatus && (
                  <Popover
                    trigger="click"
                    open={openMenu === 'bulk'}
                    onOpenChange={(o) => setOpenMenu(o ? 'bulk' : null)}
                    content={<StatusMenu settable={settable} onSelect={bulkSetStatus} bulk />}
                  >
                    <button type="button" style={barBtn}>
                      Set status <DownOutlined style={{ fontSize: 9, color: 'var(--muted)' }} />
                    </button>
                  </Popover>
                )}
                <Permission required={'DELETE_ORDER'}>
                  <button type="button" onClick={bulkDelete} style={dangerBtn}>Delete</button>
                </Permission>
              </div>
            </div>
          )}

          {/* --------------------------- Desktop / tablet table -------------------------- */}
          {!isMobile && (
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <div role="table" aria-label="Orders" aria-rowcount={meta.total} style={{ minWidth: MIN_TABLE_WIDTH }}>
                <div role="rowgroup">
                  <div role="row" style={{
                    display: 'grid', gridTemplateColumns: grid, background: 'var(--surface2)',
                    borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, zIndex: 5,
                  }}>
                    <div role="columnheader" style={{ padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <input
                        type="checkbox"
                        aria-label="Select all rows on this page"
                        checked={allOnPageSelected}
                        onChange={toggleSelectAll}
                        style={checkboxStyle}
                      />
                    </div>
                    {visibleCols.map((h) => {
                      const sorted = sortField === h.key;
                      const active = filterActive(h.filter);
                      return (
                        <div
                          key={h.key}
                          role="columnheader"
                          aria-sort={h.sortable ? (sorted ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none') : undefined}
                          style={{
                            padding: '8px', display: 'flex', alignItems: 'center', gap: 5, minWidth: 0,
                            font: '700 11.5px Outfit, sans-serif',
                            color: sorted || active ? 'var(--activeText)' : 'var(--muted)',
                            letterSpacing: '.05em', textTransform: 'uppercase',
                            justifyContent: h.align === 'right' ? 'flex-end' : 'flex-start',
                          }}
                        >
                          <span
                            className={h.sortable ? 'et-orders__sortable' : undefined}
                            onClick={h.sortable ? () => toggleSort(h.key) : undefined}
                            title={h.sortable ? `Sort by ${h.label}` : h.label}
                            style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0, cursor: h.sortable ? 'pointer' : 'default' }}
                          >
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.label}</span>
                            {/* A dim ⇅ marks what can be sorted; its absence on the
                                others is information too. */}
                            {h.sortable && (
                              sorted
                                ? <span style={{ color: PRIMARY, fontSize: 9 }}>{sortOrder === 'asc' ? '▲' : '▼'}</span>
                                : <span className="et-orders__sorticon" style={{ color: 'var(--dim)', fontSize: 9 }}>⇅</span>
                            )}
                          </span>
                          {h.filter && (
                            <Popover
                              trigger="click"
                              placement="bottom"
                              open={openFilterKey === h.key}
                              onOpenChange={(o) => setOpenFilterKey(o ? h.key : null)}
                              content={renderFilter(h)}
                            >
                              <span
                                onClick={(e) => e.stopPropagation()}
                                title={`Filter ${h.label}`}
                                style={{ marginLeft: 'auto', flexShrink: 0, cursor: 'pointer', paddingLeft: 2, color: active ? PRIMARY : 'var(--dim)' }}
                              >
                                {h.filter === 'status' ? <FilterOutlined /> : h.filter === 'delivery' ? <CalendarOutlined /> : <SearchOutlined />}
                              </span>
                            </Popover>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div role="rowgroup">
                  {loading && [...Array(7)].map((_, r) => (
                    <div key={r} role="row" style={{ display: 'grid', gridTemplateColumns: grid, borderBottom: '1px solid var(--border)', minHeight: rowMinHeight, alignItems: 'center' }}>
                      <div role="gridcell" />
                      {visibleCols.map((h) => (
                        <div key={h.key} role="gridcell" style={{ padding: '0 8px', minWidth: 0 }}>
                          <div className="et-orders__skel" style={{ height: 10, width: '72%' }} />
                        </div>
                      ))}
                    </div>
                  ))}

                  {showEmpty && (
                    <EmptyState count={activeFilters.length} onClear={() => { clearFilters(); clearSort(); }} />
                  )}

                  {showRows && orders.map((o, idx) => {
                    const isLastViewed = o.id === lastViewedId;
                    const isSelected = !!selected[o.id];
                    const late = isOverdue(o.deliveryDate, o.status);
                    return (
                      <div
                        key={o.id}
                        role="row"
                        aria-selected={isSelected}
                        tabIndex={focusedRow === idx ? 0 : -1}
                        onFocus={() => setFocusedRow(idx)}
                        onKeyDown={(e) => onRowKeyDown(e, idx, o)}
                        ref={(el) => {
                          rowRefs.current[idx] = el;
                          if (isLastViewed) lastViewedRef.current = el;
                        }}
                        className="et-orders__row"
                        style={{
                          display: 'grid', gridTemplateColumns: grid, borderBottom: '1px solid var(--border)',
                          minHeight: rowMinHeight, alignItems: 'center',
                          background: isSelected ? 'var(--activeBg)' : isLastViewed ? 'var(--hover)' : 'transparent',
                          boxShadow: isSelected || isLastViewed ? 'inset 3px 0 0 var(--primary)' : 'none',
                          outline: 'none',
                        }}
                      >
                        <div role="gridcell" style={{ padding: '0 8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <input
                            type="checkbox"
                            aria-label={`Select order #${o.id}`}
                            checked={isSelected}
                            onChange={() => toggleSelect(o.id)}
                            style={checkboxStyle}
                          />
                        </div>

                        {visibleCols.map((h) => (
                          <div
                            key={h.key}
                            role="gridcell"
                            style={{
                              padding: cellPad, display: 'flex', alignItems: 'center', minWidth: 0,
                              justifyContent: h.align === 'right' ? 'flex-end' : 'flex-start',
                            }}
                          >
                            {renderCell(h.key, o, {
                              compact, late, canChangeStatus, settable,
                              statusPopoverId, setStatusPopoverId, setOneStatus,
                              onView: (e) => openPanel(o.id, e.currentTarget),
                              onEdit: () => handleEdit(o),
                              onDelete: () => handleDelete(o),
                              onOpenLink: () => setLastViewedId(o.id),
                            })}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ------------------------------- Mobile cards ------------------------------- */}
          {isMobile && (
            <div role="list" aria-label="Orders" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              {loading && [...Array(5)].map((_, r) => (
                <div key={r} style={{ padding: '13px 14px', borderBottom: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 9 }}>
                  <div className="et-orders__skel" style={{ height: 12, width: '55%' }} />
                  <div className="et-orders__skel" style={{ height: 12, width: '80%' }} />
                  <div className="et-orders__skel" style={{ height: 10, width: '40%' }} />
                </div>
              ))}

              {showEmpty && <EmptyState count={activeFilters.length} onClear={() => { clearFilters(); clearSort(); }} />}

              {showRows && orders.map((o) => (
                <OrderCard
                  key={o.id}
                  order={o}
                  selectMode={selectMode}
                  selected={!!selected[o.id]}
                  onToggleSelect={() => toggleSelect(o.id)}
                  onOpen={(e) => openPanel(o.id, e.currentTarget)}
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          <div style={{
            padding: '12px 18px', borderTop: '1px solid var(--border)', display: 'flex',
            alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ font: '400 12px Outfit, sans-serif', color: 'var(--muted)' }}>
                {meta.total === 0 ? '0 results' : (
                  <>Showing <span style={{ fontFamily: 'var(--mono)', color: 'var(--text)' }}>{rangeStart}–{rangeEnd}</span> of <span style={{ fontFamily: 'var(--mono)', color: 'var(--text)' }}>{meta.total}</span></>
                )}
              </div>
              <Popover
                trigger="click"
                open={openMenu === 'rows'}
                onOpenChange={(o) => setOpenMenu(o ? 'rows' : null)}
                content={<MenuList items={PAGE_SIZE_OPTIONS.map((n) => ({ key: n, label: String(n) }))} active={pageSize} onSelect={changePageSize} />}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer' }}>
                  <span style={{ font: '400 12px Outfit, sans-serif', color: 'var(--muted)' }}>Rows</span>
                  <span style={{ height: 28, padding: '0 9px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', font: '600 12px var(--mono)', display: 'flex', alignItems: 'center', gap: 8 }}>
                    {pageSize}<DownOutlined style={{ fontSize: 9, color: 'var(--muted)' }} />
                  </span>
                </span>
              </Popover>
            </div>

            {isMobile ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%' }}>
                <button type="button" disabled={page <= 1} onClick={() => goToPage(page - 1)} style={{ ...pagerBtn, flex: 1, opacity: page <= 1 ? 0.5 : 1 }}>‹ Prev</button>
                <span style={{ height: 38, padding: '0 12px', borderRadius: 9, background: 'var(--surface2)', border: '1px solid var(--border)', font: '600 12.5px var(--mono)', display: 'flex', alignItems: 'center' }}>
                  {page} / {totalPages}
                </span>
                <button type="button" disabled={page >= totalPages} onClick={() => goToPage(page + 1)} style={{ ...pagerBtn, flex: 1, background: page >= totalPages ? 'var(--surface2)' : PRIMARY, borderColor: page >= totalPages ? 'var(--border)' : PRIMARY, color: page >= totalPages ? 'var(--dim)' : '#fff' }}>Next ›</button>
              </div>
            ) : (
              <Pagination page={page} totalPages={totalPages} onGo={goToPage} />
            )}
          </div>
        </div>

        {/* Mobile filter sheet — the same predicates the header popovers apply */}
        <Modal
          open={openMenu === 'mobileFilters'}
          onCancel={() => setOpenMenu(null)}
          title="Filters"
          footer={null}
          width={420}
        >
          <MobileFilters
            text={text}
            statusFilter={statusFilter}
            deliveryRange={deliveryRange}
            channel={channel}
            teamId={teamId}
            teamOptions={teamOptions}
            onApply={(next) => {
              setText(next.text);
              setStatusFilter(next.statusFilter);
              setDeliveryRange(next.deliveryRange);
              setChannel(next.channel);
              setTeamId(next.teamId);
              setActiveCard(null);
              resetPage();
              setOpenMenu(null);
            }}
            onClear={() => { clearFilters(); setOpenMenu(null); }}
          />
        </Modal>

        {/* The one status dialog, shared with the order page */}
        <StatusChangeModal
          open={statusModalOpen}
          order={statusTarget}
          permissions={permissions}
          initialStatus={statusPreset}
          submitting={statusSubmitting}
          onCancel={() => setStatusModalOpen(false)}
          onSubmit={submitStatusChange}
        />

        {/* Slide-out detail panel */}
        {panelId != null && (
          <>
            <div onClick={closePanel} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 90, animation: 'et-fadein .15s ease-out' }} />
            <div
              ref={panelRef}
              role="dialog"
              aria-modal="true"
              aria-label={`Order #${panelId}`}
              onKeyDown={trapTab}
              style={{
                position: 'fixed', top: 0, right: 0, height: '100%', width: '100%', maxWidth: 460,
                background: 'var(--surface)', borderLeft: '1px solid var(--border)', zIndex: 95,
                display: 'flex', flexDirection: 'column', boxShadow: '0 12px 40px rgba(0,0,0,.35)',
                animation: 'et-slidein .2s ease-out', color: 'var(--text)', fontFamily: 'Outfit, sans-serif',
                ...rootStyle,
              }}
            >
              {panelLoading || !panelOrder ? (
                <div style={{ padding: 24 }}><Skeleton active paragraph={{ rows: 10 }} /></div>
              ) : (
                <OrderPanel
                  order={panelOrder}
                  settable={settable}
                  canChangeStatus={canChangeStatus}
                  closeRef={panelCloseRef}
                  onClose={closePanel}
                  onSetStatus={(val) => setOneStatus(panelOrder.id, val)}
                  onOpenFullPage={() => openFullPage(panelOrder.id)}
                />
              )}
            </div>
          </>
        )}

        {contextHolder}
      </ConfigProvider>
    </div>
  );
}

/* ------------------------------- Row cells -------------------------------- */

function renderCell(key, o, ctx) {
  switch (key) {
    case 'id':
      return (
        <Link
          to={`/orders/${o.id}`}
          onClick={ctx.onOpenLink}
          title="Open full details page"
          style={{ font: '600 12.5px var(--mono)', color: 'var(--activeText)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          #{o.id}
        </Link>
      );
    case 'channel':
      return <ChannelBadge orderType={o.orderType} marketplace={o.marketplace} compact={ctx.compact} />;
    case 'orderedBy':
      return <span title={o.orderedBy} style={{ font: '500 13px Outfit, sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.orderedBy}</span>;
    case 'team':
      return <span title={o.team || ''} style={{ font: '400 12.5px Outfit, sans-serif', color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.team || '—'}</span>;
    case 'createdBy':
      return <span title={o.createdBy} style={{ font: '400 12.5px Outfit, sans-serif', color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.createdBy}</span>;
    case 'createdAt':
      return <span title={fmtDate(o.createdAt)} style={{ font: '400 12.5px var(--mono)', color: 'var(--text)', whiteSpace: 'nowrap' }}>{fmtDateCell(o.createdAt)}</span>;
    case 'deliveryDate':
      return (
        <span
          title={ctx.late ? `${fmtDate(o.deliveryDate)} — overdue` : fmtDate(o.deliveryDate)}
          style={{ font: '400 12.5px var(--mono)', color: ctx.late ? STATUS.error : 'var(--text)', whiteSpace: 'nowrap' }}
        >
          {fmtDateCell(o.deliveryDate)}
        </span>
      );
    case 'totalAmount':
      return <span style={{ font: '600 13px var(--mono)', whiteSpace: 'nowrap' }}>{fmtBDT(o.totalAmount)}</span>;
    case 'status':
      return ctx.canChangeStatus ? (
        <Popover
          trigger="click"
          placement="bottomLeft"
          open={ctx.statusPopoverId === o.id}
          onOpenChange={(v) => ctx.setStatusPopoverId(v ? o.id : null)}
          content={<StatusMenu settable={ctx.settable} current={o.status} onSelect={(val) => ctx.setOneStatus(o.id, val)} />}
        >
          <StatusBadge status={o.status} compact={ctx.compact} interactive title={`${statusMeta(o.status).label} — change status`} />
        </Popover>
      ) : (
        <StatusBadge status={o.status} compact={ctx.compact} />
      );
    case 'actions':
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconBtn title="View" onClick={ctx.onView}><EyeOutlined /></IconBtn>
          <Permission required={'EDIT_ORDER'}>
            <IconBtn title="Edit" onClick={ctx.onEdit}><EditOutlined /></IconBtn>
          </Permission>
          <Permission required={'DELETE_ORDER'}>
            <IconBtn title="Delete" danger onClick={ctx.onDelete}><DeleteOutlined /></IconBtn>
          </Permission>
        </div>
      );
    default:
      return null;
  }
}

/* ------------------------------ Mobile card ------------------------------- */

// A card is not a row, so it is a listitem: #id + channel + total, then the
// customer and status, then team and delivery. Submitted-by and created-at fold
// into the panel a tap away.
function OrderCard({ order: o, selectMode, selected, onToggleSelect, onOpen }) {
  const late = isOverdue(o.deliveryDate, o.status);
  return (
    <div
      role="listitem"
      tabIndex={0}
      onClick={(e) => { if (selectMode) onToggleSelect(); else onOpen(e); }}
      onKeyDown={(e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        if (selectMode) onToggleSelect(); else onOpen(e);
      }}
      style={{
        padding: '13px 14px', borderBottom: '1px solid var(--border)', display: 'flex',
        flexDirection: 'column', gap: 8, cursor: 'pointer',
        background: selected ? 'var(--activeBg)' : 'transparent',
        boxShadow: selected ? 'inset 3px 0 0 var(--primary)' : 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        {selectMode && (
          <input type="checkbox" checked={selected} readOnly aria-label={`Select order #${o.id}`} style={checkboxStyle} />
        )}
        <span style={{ font: '600 13px var(--mono)', color: 'var(--activeText)' }}>#{o.id}</span>
        <ChannelBadge orderType={o.orderType} marketplace={o.marketplace} compact />
        <span style={{ marginLeft: 'auto', font: '700 14px var(--mono)' }}>{fmtBDT(o.totalAmount)}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <span style={{ font: '500 14px Outfit, sans-serif', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {o.orderedBy}
        </span>
        <StatusBadge status={o.status} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, font: '400 12px Outfit, sans-serif', color: 'var(--muted)' }}>
        <span>{o.team || '—'}</span>
        <span style={{ color: 'var(--dim)' }}>·</span>
        <span>Due <span style={{ fontFamily: 'var(--mono)', color: late ? STATUS.error : 'var(--text)' }}>{fmtDateCell(o.deliveryDate)}</span></span>
      </div>
    </div>
  );
}

/* --------------------------------- Chrome --------------------------------- */

const primaryBtn = {
  height: 38, padding: '0 18px', borderRadius: 9, border: 'none', background: PRIMARY, color: '#fff',
  font: '700 13px Outfit, sans-serif', cursor: 'pointer',
};
const filterBtn = {
  height: 32, padding: '0 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface2)',
  color: 'var(--text)', font: '600 12px Outfit, sans-serif', cursor: 'pointer', display: 'flex',
  alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
};
const barBtn = {
  height: 30, padding: '0 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)',
  color: 'var(--text)', font: '600 12.5px Outfit, sans-serif', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7,
};
const dangerBtn = {
  height: 30, padding: '0 12px', borderRadius: 8, border: `1px solid ${STATUS.error}59`, background: 'rgba(239,68,68,.1)',
  color: STATUS.error, font: '600 12.5px Outfit, sans-serif', cursor: 'pointer',
};
const linkBtn = {
  border: 'none', background: 'none', padding: 0, color: 'var(--activeText)',
  font: '600 12.5px Outfit, sans-serif', cursor: 'pointer',
};
const pagerBtn = {
  height: 38, borderRadius: 9, border: '1px solid var(--border)', background: 'var(--surface2)',
  color: 'var(--text)', font: '600 13px Outfit, sans-serif', cursor: 'pointer',
};
const checkboxStyle = { width: 15, height: 15, accentColor: PRIMARY, cursor: 'pointer', flexShrink: 0 };

function KpiCard({ kpi: k, active, onClick }) {
  return (
    <div
      className="et-orders__kpi"
      role="button"
      tabIndex={0}
      aria-pressed={active}
      aria-label={active ? `Clear filter: ${k.label}` : `Filter orders: ${k.label}`}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      style={{
        background: 'var(--surface)', border: `1px solid ${active ? k.tint : 'var(--border)'}`,
        boxShadow: active ? `0 0 0 1px ${k.tint}` : 'none', borderRadius: 14,
        padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 5, cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <span style={{ width: 19, height: 19, borderRadius: 6, background: hexToRgba(k.tint, 0.16), color: k.tint, fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {k.icon}
        </span>
        <span style={{ font: '600 11.5px Outfit, sans-serif', color: 'var(--muted)', letterSpacing: '.02em', textTransform: 'uppercase' }}>
          {k.label}
        </span>
        {active && (
          <span style={{ marginLeft: 'auto', font: '600 11px Outfit, sans-serif', color: k.tint, display: 'flex', alignItems: 'center', gap: 4 }}>
            Filtered <CloseOutlined style={{ fontSize: 9 }} />
          </span>
        )}
      </div>
      <div style={{ font: '700 18px var(--mono)', color: 'var(--text)' }}>{Number(k.value || 0).toLocaleString()}</div>
    </div>
  );
}

/** Overview's segmented control: container --surface2, radius 8, items 28px. */
function Segmented({ value, onChange, options }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 3, padding: 3, borderRadius: 8, background: 'var(--surface2)', border: '1px solid var(--border)' }}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <div
            key={o.value}
            role="button"
            tabIndex={0}
            aria-pressed={on}
            onClick={() => onChange(o.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChange(o.value); } }}
            style={{
              height: 28, padding: '0 12px', borderRadius: 6,
              background: on ? PRIMARY : 'transparent', color: on ? '#fff' : 'var(--muted)',
              font: '600 12px Outfit, sans-serif', display: 'flex', alignItems: 'center', cursor: 'pointer',
            }}
          >
            {o.label}
          </div>
        );
      })}
    </div>
  );
}

function Chip({ label, value, onClear, tint, tone, mono }) {
  const isTinted = !!tint;
  const isSort = tone === 'sort';
  const bg = isTinted ? hexToRgba(tint, 0.12) : isSort ? 'var(--activeBg)' : 'var(--surface2)';
  const border = isTinted ? `1px solid ${hexToRgba(tint, 0.35)}` : isSort ? `1px solid ${hexToRgba(PRIMARY, .4)}` : '1px solid var(--border)';
  const color = isTinted ? tint : isSort ? 'var(--activeText)' : 'var(--text)';
  return (
    <span style={{
      flexShrink: 0, height: 26, padding: '0 5px 0 9px', borderRadius: 7, background: bg, border, color,
      font: '600 12px Outfit, sans-serif', display: 'flex', alignItems: 'center', gap: 7,
    }}>
      {label && <span style={{ fontWeight: 500, opacity: 0.8, color: isTinted || isSort ? 'inherit' : 'var(--muted)' }}>{label}</span>}
      <span style={mono ? { fontFamily: 'var(--mono)', fontSize: 11.5 } : undefined}>{value}</span>
      <span
        role="button"
        tabIndex={0}
        aria-label={`Remove ${label || ''} ${value} filter`}
        onClick={onClear}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClear(); } }}
        style={{ width: 16, height: 16, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, cursor: 'pointer', background: isTinted || isSort ? 'rgba(255,255,255,.12)' : 'var(--surface)' }}
      >
        <CloseOutlined />
      </span>
    </span>
  );
}

function IconBtn({ children, onClick, title, danger }) {
  return (
    <button
      type="button"
      data-row-action
      className={danger ? 'et-orders__iconbtn et-orders__iconbtn--danger' : 'et-orders__iconbtn'}
      title={title}
      aria-label={title}
      onClick={(e) => { e.stopPropagation(); onClick(e); }}
      style={{
        width: 24, height: 24, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: 6, border: 'none', background: 'transparent', color: 'var(--dim)', fontSize: 12, cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function MenuList({ items, active, onSelect, hint }) {
  return (
    <div style={{ minWidth: 168, background: 'var(--surface2)', borderRadius: 8, overflow: 'hidden', maxHeight: 320, overflowY: 'auto' }}>
      <div style={{ padding: 6 }}>
        {items.map((it) => {
          const isActive = active !== undefined && active === it.key;
          return (
            <div
              key={String(it.key)}
              onClick={() => onSelect(it.key)}
              style={{ height: 34, borderRadius: 8, padding: '0 10px', display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer', background: isActive ? 'var(--hover)' : 'transparent' }}
            >
              {it.dot && <span style={{ color: it.dot, fontSize: 11 }}>●</span>}
              <span style={{ font: '600 12.5px Outfit, sans-serif', color: 'var(--text)' }}>{it.label}</span>
              {isActive && <CheckOutlined style={{ marginLeft: 'auto', color: PRIMARY, fontSize: 11 }} />}
            </div>
          );
        })}
      </div>
      {hint && (
        <div style={{ padding: '9px 12px', borderTop: '1px solid var(--border)', font: '400 11.5px Outfit, sans-serif', color: 'var(--dim)', lineHeight: 1.45 }}>
          {hint}
        </div>
      )}
    </div>
  );
}

/**
 * The one status control: bulk bar, row badge and panel all open this.
 *
 * `bulk` greys out the exits. Cancelling, returning or holding an order needs a
 * reason and, for produced goods, a Sellable/Damaged decision per line — none of
 * which can be answered once for a whole selection, so those go through the
 * per-order modal instead (ADR 0006).
 */
function StatusMenu({ settable, current, onSelect, bulk = false }) {
  return (
    <div style={{ minWidth: 176, background: 'var(--surface2)', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ padding: '8px 12px 6px', font: '700 10.5px Outfit, sans-serif', color: 'var(--dim)', textTransform: 'uppercase', letterSpacing: '.05em', borderBottom: '1px solid var(--border)' }}>
        Change status
      </div>
      <div style={{ padding: 6 }}>
        {settable.map((s) => {
          const isCurrent = s.value === current;
          const isExit = bulk && EXIT_STATUSES.includes(s.value);
          const disabled = isCurrent || isExit;
          return (
            <button
              key={s.value}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(s.value)}
              title={isExit ? 'Open the order — this needs a reason' : undefined}
              style={{
                width: '100%', height: 32, borderRadius: 8, padding: '0 10px', border: 'none',
                background: isCurrent ? 'var(--hover)' : 'transparent', textAlign: 'left',
                display: 'flex', alignItems: 'center', gap: 9,
                cursor: disabled ? 'default' : 'pointer',
                opacity: isExit ? 0.45 : 1,
              }}
            >
              <span style={{ width: 6, height: 6, borderRadius: 3, background: s.color, flexShrink: 0 }} />
              <span style={{ font: '600 12.5px Outfit, sans-serif', color: s.color, flex: 1 }}>{s.label}</span>
              {isCurrent && <CheckOutlined style={{ color: 'var(--dim)', fontSize: 11 }} />}
            </button>
          );
        })}
        {bulk && settable.some((s) => EXIT_STATUSES.includes(s.value)) && (
          <div style={{ padding: '6px 10px 2px', font: '400 11px Outfit, sans-serif', color: 'var(--dim)' }}>
            Cancel, return and hold need a reason — open the order.
          </div>
        )}
        {settable.length === 0 && (
          <div style={{ padding: '8px 10px', font: '400 12px Outfit, sans-serif', color: 'var(--dim)' }}>No statuses available.</div>
        )}
      </div>
    </div>
  );
}

function ColumnsMenu({ hidden, onToggle, onReset }) {
  return (
    <div style={{ width: 220, background: 'var(--surface2)', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ padding: '9px 12px', borderBottom: '1px solid var(--border)', font: '700 11px Outfit, sans-serif', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>
        Columns
      </div>
      <div style={{ padding: 6, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {COLUMNS.map((c) => {
          const on = !hidden.includes(c.key);
          return (
            <div
              key={c.key}
              onClick={c.locked ? undefined : () => onToggle(c.key)}
              style={{
                height: 30, padding: '0 8px', display: 'flex', alignItems: 'center', gap: 9, borderRadius: 6,
                font: '500 12.5px Outfit, sans-serif', color: c.locked ? 'var(--dim)' : 'var(--text)',
                cursor: c.locked ? 'default' : 'pointer',
              }}
            >
              <span style={{
                width: 14, height: 14, borderRadius: 4, flexShrink: 0, fontSize: 9,
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                background: on ? (c.locked ? 'var(--borderStrong)' : PRIMARY) : 'transparent',
                border: on ? 'none' : '1.5px solid var(--borderStrong)',
              }}>
                {on ? '✓' : ''}
              </span>
              {c.label}
              {c.locked && <span style={{ marginLeft: 'auto', font: '600 10px Outfit, sans-serif' }}>locked</span>}
            </div>
          );
        })}
      </div>
      <div onClick={onReset} style={{ padding: '8px 12px', borderTop: '1px solid var(--border)', background: 'var(--surface)', font: '600 12px Outfit, sans-serif', color: 'var(--activeText)', cursor: 'pointer' }}>
        Reset to default
      </div>
    </div>
  );
}

/** Numbered pages with ellipsis and first/last, plus a jump field past 10 pages. */
function Pagination({ page, totalPages, onGo }) {
  const [jump, setJump] = useState('');
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
      <PageBtn disabled={page <= 1} onClick={() => onGo(page - 1)} label="Previous page">‹</PageBtn>
      {pages.map((p, i, arr) => (
        <React.Fragment key={p}>
          {i > 0 && arr[i - 1] !== p - 1 && (
            <span style={{ height: 30, minWidth: 22, color: 'var(--dim)', font: '600 12px Outfit, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>…</span>
          )}
          <PageBtn active={p === page} onClick={() => onGo(p)} label={`Page ${p}`}>{p}</PageBtn>
        </React.Fragment>
      ))}
      <PageBtn disabled={page >= totalPages} onClick={() => onGo(page + 1)} label="Next page">›</PageBtn>
      {totalPages > 10 && (
        <>
          <span style={{ width: 1, height: 20, background: 'var(--border)', margin: '0 5px' }} />
          <span style={{ font: '400 12px Outfit, sans-serif', color: 'var(--muted)' }}>Go to</span>
          <Input
            size="small"
            placeholder="page"
            value={jump}
            onChange={(e) => setJump(e.target.value.replace(/\D/g, ''))}
            onPressEnter={() => { if (jump) { onGo(Number(jump)); setJump(''); } }}
            style={{ width: 58, height: 30, fontFamily: MONO }}
          />
        </>
      )}
    </div>
  );
}

function PageBtn({ children, onClick, active, disabled, label }) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      disabled={disabled}
      onClick={onClick}
      style={{
        height: 30, minWidth: 30, padding: '0 9px', borderRadius: 8,
        border: active ? 'none' : '1px solid var(--border)',
        background: active ? PRIMARY : 'var(--surface2)',
        color: active ? '#fff' : disabled ? 'var(--dim)' : 'var(--text)',
        font: `${active ? 700 : 600} 12px ${MONO}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {children}
    </button>
  );
}

/** Overview's 46px glyph tile — no emoji. */
function EmptyState({ count, onClear }) {
  return (
    <div style={{ padding: '34px 18px 38px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center' }}>
      <div style={{ width: 46, height: 46, borderRadius: 13, background: 'var(--surface2)', border: '1px solid var(--border)', color: 'var(--dim)', fontSize: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        ▤
      </div>
      <div style={{ font: '600 13.5px Outfit, sans-serif' }}>
        {count > 0 ? 'No orders match these filters' : 'No orders yet'}
      </div>
      <div style={{ font: '400 12.5px Outfit, sans-serif', color: 'var(--muted)', maxWidth: 280 }}>
        {count > 0
          ? `${count} filter${count === 1 ? ' is' : 's are'} applied. Remove one from the strip above, or clear them all.`
          : 'Orders you create will show up here.'}
      </div>
      {count > 0 && (
        <button type="button" onClick={onClear} style={{ marginTop: 4, height: 32, padding: '0 13px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', font: '600 12.5px Outfit, sans-serif', cursor: 'pointer' }}>
          Clear all filters
        </button>
      )}
    </div>
  );
}

/* ----------------------------- Detail panel ------------------------------- */

function OrderPanel({ order, settable = [], canChangeStatus, closeRef, onClose, onSetStatus, onOpenFullPage }) {
  const [statusOpen, setStatusOpen] = useState(false);
  // Read here rather than threaded down from Orders: the panel is the only part
  // of this screen that prints an invoice.
  const { company } = useStateContext();
  // Offline (ex-merchant) orders carry no marketplace; both channels have a customer.
  const isOffline = order.orderType === OrderTypeEnum.MERCHANT;
  const channelName = order.marketplace?.name || (isOffline ? 'Offline' : 'Online');
  const products = order.products || [];
  const timeline = [...(order.orderStatusChanges || [])].reverse();
  const customerAddress = order.customer
    ? [order.customer.address?.address, order.customer.address?.upazila?.name, order.customer.address?.district?.name].filter(Boolean).join(', ')
    : '';

  const sectionLabel = { font: '700 11px Outfit, sans-serif', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8 };

  return (
    <>
      <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div style={{ font: '700 16px var(--mono)' }}>#{order.id}</div>
          <div style={{ font: '400 12.5px Outfit, sans-serif', color: 'var(--muted)', marginTop: 2 }}>
            {order.customer?.name || channelName} · {fmtDateTime(order.createdAt)}
          </div>
          <button type="button" onClick={onOpenFullPage} style={{ ...linkBtn, marginTop: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
            Open full details page <ArrowRightOutlined style={{ fontSize: 10 }} />
          </button>
        </div>
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          aria-label="Close panel"
          style={{ width: 28, height: 28, borderRadius: 7, border: 'none', background: 'var(--surface2)', color: 'var(--muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
        >
          <CloseOutlined />
        </button>
      </div>

      <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ font: '400 12px Outfit, sans-serif', color: 'var(--muted)' }}>Status</span>
        {canChangeStatus ? (
          <Popover
            trigger="click"
            placement="bottomLeft"
            open={statusOpen}
            onOpenChange={setStatusOpen}
            content={<StatusMenu settable={settable} current={order.status} onSelect={(val) => { setStatusOpen(false); onSetStatus(val); }} />}
          >
            <StatusBadge status={order.status} interactive />
          </Popover>
        ) : (
          <StatusBadge status={order.status} />
        )}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div>
          <div style={sectionLabel}>Items</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {products.map((p) => (
              <div key={p.id} style={{ display: 'flex', gap: 10, padding: 10, border: '1px solid var(--border)', borderRadius: 10 }}>
                <div style={{ width: 40, height: 40, borderRadius: 7, background: 'var(--surface2)', color: 'var(--muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', font: '700 13px Outfit, sans-serif', flexShrink: 0 }}>
                  {p.productType?.name?.[0] || '?'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ font: '600 13px Outfit, sans-serif' }}>{p.productType?.name}</span>
                    <span style={{ font: '600 13px var(--mono)' }}>{fmtBDT(p.price)}</span>
                  </div>
                  <div style={{ font: '400 11.5px Outfit, sans-serif', color: 'var(--activeText)', marginTop: 2 }}>
                    {[p.fabrics?.name, p.fabricType?.name, p.productFormation?.name].filter(Boolean).join(' · ')} · Qty {p.unit}
                  </div>
                </div>
              </div>
            ))}
            {products.length === 0 && <div style={{ font: '400 12.5px Outfit, sans-serif', color: 'var(--dim)' }}>No items.</div>}
          </div>
        </div>

        <div>
          <div style={sectionLabel}>{isOffline ? 'Customer · Offline' : 'Customer · Online'}</div>
          <div style={{ font: '600 13px Outfit, sans-serif' }}>{order.customer?.name}</div>
          {customerAddress && <div style={{ font: '400 12px Outfit, sans-serif', color: 'var(--muted)', marginTop: 2 }}>{customerAddress}</div>}
          <div style={{ font: '400 12px var(--mono)', color: 'var(--muted)' }}>{order.customer?.phone || order.customer?.address?.phone}</div>
          {order.customer?.facebook && (
            <div style={{ font: '400 12px Outfit, sans-serif', color: 'var(--muted)', marginTop: 2 }}>{isOffline ? 'Ref' : 'FB'}: {order.customer.facebook}</div>
          )}
          {order.marketplace?.name && <div style={{ font: '400 12px Outfit, sans-serif', color: 'var(--muted)', marginTop: 2 }}>Channel: {order.marketplace.name}</div>}
        </div>

        <div>
          <div style={sectionLabel}>Delivery</div>
          <div style={{ font: '400 12.5px Outfit, sans-serif' }}>{order.deliveryChannel?.name || '—'} · {fmtDate(order.deliveryDate)}</div>
        </div>

        <div>
          <div style={sectionLabel}>Payment</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', font: '400 12.5px Outfit, sans-serif' }}><span>Subtotal</span><span style={{ fontFamily: 'var(--mono)' }}>{fmtBDT(order.payment?.amount)}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', font: '400 12.5px Outfit, sans-serif', marginTop: 2 }}><span>Delivery</span><span style={{ fontFamily: 'var(--mono)' }}>{fmtBDT(order.payment?.deliveryCharge)}</span></div>
          <div style={{ height: 1, background: 'var(--border)', margin: '8px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', font: '700 15px Outfit, sans-serif' }}><span>Total</span><span style={{ fontFamily: 'var(--mono)' }}>{fmtBDT(order.payment?.totalAmount)}</span></div>
        </div>

        <div>
          <div style={sectionLabel}>History</div>
          {timeline.length ? timeline.map((t, i) => {
            const info = statusMeta(t.status);
            return (
              <div key={i} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ width: 6, height: 6, borderRadius: 3, background: info.color, marginTop: 5, flexShrink: 0 }} />
                <div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ font: '700 10.5px Outfit, sans-serif', color: info.color }}>{info.label}</span>
                    <span style={{ font: '400 10.5px var(--mono)', color: 'var(--muted)' }}>{fmtDateTime(t.created_at)}</span>
                  </div>
                  {t.comment && <div style={{ font: '400 12px Outfit, sans-serif', marginTop: 2 }}>{t.comment}</div>}
                </div>
              </div>
            );
          }) : <div style={{ font: '400 12.5px Outfit, sans-serif', color: 'var(--dim)' }}>No history yet.</div>}
        </div>

        <PDFDownloadLink document={<OrderInvoice order={order} company={company} />} fileName={`order-${order.id}-${dayjs().unix()}.pdf`}>
          {({ loading }) => (
            <button type="button" style={{
              height: 40, width: '100%', borderRadius: 9, border: `1.5px dashed ${PRIMARY}`, background: 'transparent',
              color: PRIMARY, font: '700 12.5px Outfit, sans-serif', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}>
              <DownloadOutlined /> {loading ? 'Preparing…' : 'Download PDF'}
            </button>
          )}
        </PDFDownloadLink>
      </div>
    </>
  );
}

/* ------------------------ Column-header filter bodies ---------------------- */

function TextFilterDropdown({ committed, placeholder, onApply, onReset }) {
  const [val, setVal] = useState(committed || '');
  useEffect(() => { setVal(committed || ''); }, [committed]);
  return (
    <div style={{ width: 200, padding: 4 }} onKeyDown={(e) => e.stopPropagation()}>
      <Input
        autoFocus
        size="small"
        placeholder={placeholder}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onPressEnter={() => onApply(val.trim())}
        style={{ marginBottom: 8 }}
      />
      <div style={{ display: 'flex', gap: 8 }}>
        <Button type="primary" size="small" icon={<SearchOutlined />} onClick={() => onApply(val.trim())} style={{ flex: 1 }}>Search</Button>
        <Button size="small" onClick={onReset} style={{ flex: 1 }}>Reset</Button>
      </div>
    </div>
  );
}

function StatusFilterDropdown({ committed, onApply, onReset }) {
  const [vals, setVals] = useState(committed || []);
  useEffect(() => { setVals(committed || []); }, [committed]);
  return (
    <div style={{ width: 170, padding: 4 }}>
      <Checkbox.Group value={vals} onChange={setVals} style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8, maxHeight: 260, overflowY: 'auto' }}>
        {filterableStatuses.map((s) => <Checkbox key={s.value} value={s.value}>{s.label}</Checkbox>)}
      </Checkbox.Group>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button type="primary" size="small" onClick={() => onApply(vals)} style={{ flex: 1 }}>OK</Button>
        <Button size="small" onClick={onReset} style={{ flex: 1 }}>Reset</Button>
      </div>
    </div>
  );
}

function DateFilterDropdown({ committed, onApply, onReset }) {
  const [range, setRange] = useState(committed || null);
  useEffect(() => { setRange(committed || null); }, [committed]);
  return (
    <div style={{ padding: 4 }}>
      <DatePicker.RangePicker size="small" value={range} onChange={setRange} style={{ marginBottom: 8, display: 'flex' }} />
      <div style={{ display: 'flex', gap: 8 }}>
        <Button type="primary" size="small" icon={<SearchOutlined />} onClick={() => onApply(range)} style={{ flex: 1 }}>Filter</Button>
        <Button size="small" onClick={onReset} style={{ flex: 1 }}>Reset</Button>
      </div>
    </div>
  );
}

// Single-choice column filter (Channel, Team). Applies immediately — there's no
// partial input to confirm, unlike the text/status/date dropdowns.
function ChoiceFilterDropdown({ committed, options, onApply }) {
  return (
    <MenuList
      items={options.map((o) => ({ key: o.value, label: o.label }))}
      active={String(committed || '')}
      onSelect={(v) => onApply(v)}
    />
  );
}

/**
 * Below 768 there is no header row to hang the filter popovers off, so all of
 * them collect into one sheet with a single Apply.
 */
function MobileFilters({ text, statusFilter, deliveryRange, channel, teamId, teamOptions, onApply, onClear }) {
  const [draft, setDraft] = useState({ text, statusFilter, deliveryRange, channel, teamId });
  const label = { font: '700 11px Outfit, sans-serif', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 8 }}>
      <div>
        <div style={label}>Order ID</div>
        <Input value={draft.text.id} onChange={(e) => setDraft((d) => ({ ...d, text: { ...d.text, id: e.target.value } }))} placeholder="e.g. 10482" />
      </div>
      <div>
        <div style={label}>Ordered by</div>
        <Input value={draft.text.orderedBy} onChange={(e) => setDraft((d) => ({ ...d, text: { ...d.text, orderedBy: e.target.value } }))} placeholder="Customer or page name" />
      </div>
      <div>
        <div style={label}>Submitted by</div>
        <Input value={draft.text.createdBy} onChange={(e) => setDraft((d) => ({ ...d, text: { ...d.text, createdBy: e.target.value } }))} placeholder="Name" />
      </div>
      <div>
        <div style={label}>Channel</div>
        <Segmented
          value={draft.channel}
          onChange={(v) => setDraft((d) => ({ ...d, channel: v }))}
          options={CHANNEL_OPTIONS.map((o) => ({ value: o.value, label: o.label.replace(' channels', '') }))}
        />
      </div>
      <div>
        <div style={label}>Team</div>
        <MenuList
          items={teamOptions.map((o) => ({ key: o.value, label: o.label }))}
          active={String(draft.teamId || '')}
          onSelect={(v) => setDraft((d) => ({ ...d, teamId: v }))}
        />
      </div>
      <div>
        <div style={label}>Status</div>
        <Checkbox.Group
          value={draft.statusFilter}
          onChange={(vals) => setDraft((d) => ({ ...d, statusFilter: vals }))}
          style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
        >
          {filterableStatuses.map((s) => <Checkbox key={s.value} value={s.value}>{s.label}</Checkbox>)}
        </Checkbox.Group>
      </div>
      <div>
        <div style={label}>Delivery date</div>
        <DatePicker.RangePicker value={draft.deliveryRange} onChange={(r) => setDraft((d) => ({ ...d, deliveryRange: r }))} style={{ width: '100%' }} />
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <Button onClick={onClear} style={{ flex: 1 }}>Clear all</Button>
        <Button type="primary" onClick={() => onApply(draft)} style={{ flex: 2 }}>Apply</Button>
      </div>
    </div>
  );
}

export default Orders;
